import * as React from "react";
import { Link } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useChangePassword } from "@/hooks/usePassword";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";

/**
 * Settings → Security card. Lets a signed-in user change their password
 * without going through the email reset flow. Falls back to the
 * forgot-password link for people who don't remember their current one.
 */
export function ChangePasswordCard() {
  const { toast } = useToast();
  const change = useChangePassword();

  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  function reset() {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (change.isPending) return;

    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New passwords don't match.");
      return;
    }
    if (next === current) {
      setError("New password must be different from the current one.");
      return;
    }

    try {
      const res = await change.mutateAsync({ currentPassword: current, newPassword: next });
      if (res.success) {
        toast({ title: "Password changed", description: res.message });
        reset();
      } else {
        setError(res.message);
      }
    } catch (err) {
      // The backend returns 400 with { message } on a bad current password
      // or a policy violation — surface that inline.
      const message =
        err instanceof ApiError ? err.message : "Couldn't change your password. Try again.";
      setError(message);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-muted-foreground" />
          Change password
        </CardTitle>
        <CardDescription>
          Use 8+ characters with an uppercase letter, a number and a symbol.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4 sm:max-w-sm">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={e => setCurrent(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={e => setNext(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
            />
          </div>
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button type="submit" disabled={change.isPending}>
              {change.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Update password
            </Button>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-muted-foreground hover:text-primary hover:underline"
            >
              Forgot your current password?
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
